/**
 * useEdgeShapePointAdd - Hook para agregar shape points con click sobre el edge
 *
 * @version 1.0
 * @created 2026-04-13
 * @module core/spatial/hooks
 *
 * @description
 * Permite agregar nuevos shape points geométricos al hacer click sobre
 * el edge durante el modo de edición geométrica.
 *
 * - Click sobre la línea del edge → inserta nuevo shape point
 * - NO hace nada si el click es sobre un handle existente
 * - NO altera topología (no divide el edge)
 *
 * @example
 * ```typescript
 * useEdgeShapePointAdd({
 *   enabled: geometryEditEdgeId !== null,
 *   edgeId: geometryEditEdgeId,
 *   onAddPoint: addShapePoint,
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import type { MapMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';

const EDGES_LAYER_ID = 'canonical-network-edges';
const SHAPE_HANDLES_LAYER_ID = 'edge-shape-handles';

export interface UseEdgeShapePointAddConfig {
  /** Si el hook está habilitado */
  enabled: boolean;
  /** ID del edge en edición geométrica */
  edgeId: string | null;
  /** Callback para agregar un shape point */
  onAddPoint: (position: [number, number]) => Promise<number | null>;
}

/**
 * Hook para agregar shape points con click sobre el edge
 */
export function useEdgeShapePointAdd({
  enabled,
  edgeId,
  onAddPoint,
}: UseEdgeShapePointAddConfig): void {
  // Refs para evitar stale closures
  const enabledRef = useRef(enabled);
  const edgeIdRef = useRef(edgeId);
  const onAddPointRef = useRef(onAddPoint);

  // Mantener refs sincronizados
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { edgeIdRef.current = edgeId; }, [edgeId]);
  useEffect(() => { onAddPointRef.current = onAddPoint; }, [onAddPoint]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const map = mapEngine.getMap();
    if (!map) {
      return;
    }

    console.log('[useEdgeShapePointAdd] 🎬 Registering click handler for edge:', edgeId);

    /**
     * Handler de click para agregar shape points
     */
    const handleClick = (e: MapMouseEvent): void => {
      if (!enabledRef.current || !edgeIdRef.current) {
        return;
      }

      // Verificar si el click fue sobre un handle existente
      // Si es así, NO agregar punto (el handle se está seleccionando para drag)
      const handleFeatures = map.queryRenderedFeatures(e.point, {
        layers: [SHAPE_HANDLES_LAYER_ID],
      });

      if (handleFeatures.length > 0) {
        console.log('[useEdgeShapePointAdd] ⏭️ Click on existing handle, skipping add');
        return;
      }

      // Verificar si el click fue sobre el edge en edición
      const edgeFeatures = map.queryRenderedFeatures(e.point, {
        layers: [EDGES_LAYER_ID],
      });

      // Buscar el edge específico que estamos editando
      const targetEdge = edgeFeatures.find(f =>
        (f.properties?.id || f.id) === edgeIdRef.current
      );

      if (!targetEdge) {
        console.log('[useEdgeShapePointAdd] ⏭️ Click not on editing edge');
        return;
      }

      // Obtener coordenadas del click
      const clickPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      console.log('[useEdgeShapePointAdd] ➕ Adding shape point at:', clickPosition);

      // Agregar el shape point
      onAddPointRef.current(clickPosition);
    };

    // Registrar listener
    map.on('click', handleClick);

    // Cambiar cursor cuando está sobre el edge (indica que puede agregar punto)
    const handleMouseMove = (e: MapMouseEvent): void => {
      if (!enabledRef.current || !edgeIdRef.current) {
        return;
      }

      // Verificar si está sobre un handle
      const handleFeatures = map.queryRenderedFeatures(e.point, {
        layers: [SHAPE_HANDLES_LAYER_ID],
      });

      if (handleFeatures.length > 0) {
        map.getCanvas().style.cursor = 'grab';
        return;
      }

      // Verificar si está sobre el edge
      const edgeFeatures = map.queryRenderedFeatures(e.point, {
        layers: [EDGES_LAYER_ID],
      });

      const targetEdge = edgeFeatures.find(f =>
        (f.properties?.id || f.id) === edgeIdRef.current
      );

      if (targetEdge) {
        map.getCanvas().style.cursor = 'crosshair';
      } else {
        map.getCanvas().style.cursor = '';
      }
    };

    map.on('mousemove', handleMouseMove);

    // Cleanup
    return () => {
      console.log('[useEdgeShapePointAdd] 🧹 Removing click handler');
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      map.getCanvas().style.cursor = '';
    };
  }, [enabled]); // Solo depende de enabled
}
